import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  writeBatch
} from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from "xlsx";
import Loader from "../components/Loader";
import Settings from "./Settings";

function Attendance() {
  const [rows, setRows] = useState([]);
  const [employeesMap, setEmployeesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // filters
  const [nameFilter, setNameFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // pagination
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("Add"); // "Add" or "Edit"
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    empId: "",
    date: "",
    checkInTime: "", // string "HH:mm"
    checkOutTime: "", // string "HH:mm"
    checkOutTime: "", // string "HH:mm"
    status: ""
  });

  // Sync Modal State
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncMonth, setSyncMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM

  // Holiday Modal State
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayDate, setHolidayDate] = useState("");

  // View State: 'records', 'register', 'settings', 'notifications'
  const [view, setView] = useState("records");
  const [registerDate, setRegisterDate] = useState(new Date().toISOString().split('T')[0]);
  const [registerData, setRegisterData] = useState({}); // { empId: { status, id, checkIn, checkOut } }
  const [employeesList, setEmployeesList] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({ workingHours: 8 });

  // Notifications State
  const [notifDateFilter, setNotifDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [notifSendingId, setNotifSendingId] = useState(null);

  const fetchAttendance = async () => {
    setLoading(true);
    // Reload logic is handled in loadData mostly, but we can reuse loadData reference or replicate logic.
    // To match original structure, let's just trigger loadData which is in useEffect.
    // However, original logic had loadData inside useEffect. Let's extract it.
    await loadDataInternal();
    setLoading(false);
  };

  const loadDataInternal = async () => {
    // 1️⃣ Load employees → map uid → name
    const empSnap = await getDocs(collection(db, "EMPLOYEES"));
    const empMap = {};
    const empList = [];
    empSnap.forEach(doc => {
      const d = doc.data();
      if (d.empId && d.category !== "Contract") {
        empMap[d.empId] = d.name;
        empList.push({ id: doc.id, ...d });
      }
    });
    setEmployeesMap(empMap);
    setEmployeesList(empList.sort((a, b) => a.name.localeCompare(b.name)));

    // 2️⃣ Load attendance
    const attSnap = await getDocs(collection(db, "attendance"));
    const att = [];
    attSnap.forEach(doc => {
      att.push({ id: doc.id, ...doc.data() });
    });

    // sort latest first
    att.sort((a, b) => b.date?.localeCompare(a.date));
    setRows(att);
  };

  useEffect(() => {
    setLoading(true);
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, "settings", "global");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setGlobalSettings(docSnap.data());
        }
      } catch (e) {
        console.error("Error fetching global settings:", e);
      }
    };
    Promise.all([loadDataInternal(), fetchSettings()]).then(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (view === "register") {
      loadRegisterData();
    }
  }, [view, registerDate, rows]);

  const loadRegisterData = () => {
    const data = {};
    employeesList.forEach(emp => {
      const existing = rows.find(r => r.empId === emp.empId && r.date === registerDate);
      data[emp.empId] = {
        status: existing ? getDisplayStatus(existing) : "",
        id: existing ? existing.id : null,
        checkIn: existing && existing.checkInTime ? existing.checkInTime.toDate().toTimeString().slice(0, 5) : "10:00",
        checkOut: existing && existing.checkOutTime ? existing.checkOutTime.toDate().toTimeString().slice(0, 5) : "18:30"
      };
    });
    setRegisterData(data);
  };

  const handleRegisterSave = async () => {
    setSaving(true);
    try {
      const batch = writeBatch(db);
      let count = 0;

      for (const empId in registerData) {
        const item = registerData[empId];
        if (!item.status) continue;

        const payload = {
          empId,
          date: registerDate,
          status: item.status,
          checkInTime: Timestamp.fromDate(new Date(`${registerDate}T${item.checkIn}`)),
          checkOutTime: Timestamp.fromDate(new Date(`${registerDate}T${item.checkOut}`)),
          updatedAt: Timestamp.now()
        };

        // Manual Hours Calc
        const diffMins = (new Date(`${registerDate}T${item.checkOut}`) - new Date(`${registerDate}T${item.checkIn}`)) / 60000;
        payload.hours = diffMins / 60;

        if (item.id) {
          batch.update(doc(db, "attendance", item.id), payload);
        } else {
          batch.set(doc(collection(db, "attendance")), payload);
        }
        count++;
      }

      await batch.commit();
      alert(`Successfully saved attendance for ${count} employees.`);
      handleRefresh();
    } catch (e) {
      console.error("Register save failed:", e);
      alert("Failed to save register.");
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchAttendance();
  }

  // ===============================
  // Per-row hours + OT calculation
  // ===============================
  const parseHoursToMinutes = (h) => {
    if (!h) return null;
    if (typeof h === "number") return Math.round(h * 60);
    const s = String(h).trim();
    if (s.includes(":")) {
      const [hh, mm] = s.split(":");
      const H = Number(hh) || 0;
      const M = Number(mm) || 0;
      return H * 60 + M;
    }
    const n = s.replace(/h$/, "").trim();
    const f = Number(n);
    if (!Number.isFinite(f)) return null;
    return Math.round(f * 60);
  };

  const computeRowHours = (r) => {
    let ci = null;
    let co = null;
    if (r.checkInTime && typeof r.checkInTime.toDate === "function") ci = r.checkInTime.toDate();
    if (r.checkOutTime && typeof r.checkOutTime.toDate === "function") co = r.checkOutTime.toDate();

    let mins = null;
    if (ci && co) {
      mins = Math.max(0, Math.round((co - ci) / 60000));
    } else if (r.hours) {
      mins = parseHoursToMinutes(r.hours);
    }

    if (mins === null) return { hours: null, ot: 0 };

    const hours = mins / 60;
    
    // Status Determination (simplified)
    const ds = r.status || (hours < 4.5 ? "A" : (hours < 5.5 ? "H" : "P"));
    
    const otThreshold = globalSettings?.workingHours || 8;
    const ot = (ds !== "A") ? Math.max(0, hours - otThreshold) : 0;
    return { hours, ot };
  };

  // derive display status
  const getDisplayStatus = (r) => {
    if (r.status) return r.status;
    const { hours } = computeRowHours(r);
    if (hours === null) return "A"; // no info -> mark absent
    if (hours < 4.5) return "A";
    if (hours >= 4.5 && hours < 5.5) return "H";
    return "P";
  };

  // ===============================
  // CRUD ACTIONS
  // ===============================

  const handleAddNew = () => {
    setModalMode("Add");
    setSelectedId(null);
    setFormData({
      empId: "",
      date: new Date().toISOString().split('T')[0],
      checkInTime: "",
      checkOutTime: "",
      status: ""
    });
    setShowModal(true);
  };

  const handleEdit = (r) => {
    setModalMode("Edit");
    setSelectedId(r.id);

    // Parse times for input value (HH:mm)
    let ci = "";
    let co = "";
    if (r.checkInTime && typeof r.checkInTime.toDate === "function") {
      ci = r.checkInTime.toDate().toTimeString().slice(0, 5);
    }
    if (r.checkOutTime && typeof r.checkOutTime.toDate === "function") {
      co = r.checkOutTime.toDate().toTimeString().slice(0, 5);
    }

    setFormData({
      empId: r.empId || "",
      date: r.date || "",
      checkInTime: ci,
      checkOutTime: co,
      status: r.status || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await deleteDoc(doc(db, "attendance", id));
      handleRefresh();
    } catch (e) {
      console.error("Error deleting document: ", e);
      alert("Failed to delete record");
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (isToSelectAll, currentRecords) => {
    if (isToSelectAll) {
      const ids = currentRecords.map(r => r.id);
      setSelectedIds(prev => [...new Set([...prev, ...ids])]);
    } else {
      const idsToRemove = currentRecords.map(r => r.id);
      setSelectedIds(prev => prev.filter(id => !idsToRemove.includes(id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected records?`)) return;

    setLoading(true);
    try {
      const batch = writeBatch(db);
      selectedIds.forEach(id => {
        batch.delete(doc(db, "attendance", id));
      });
      await batch.commit();
      alert(`Successfully deleted ${selectedIds.length} records.`);
      setSelectedIds([]);
      handleRefresh();
    } catch (e) {
      console.error("Error bulk deleting: ", e);
      alert("Failed to delete selected records.");
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.empId || !formData.date) {
      alert("Employee and Date are required!");
      return;
    }

    // Construct Timestamps
    const baseDate = formData.date; // e.g., "2023-10-27"
    let ciTs = null;
    let coTs = null;

    if (formData.checkInTime) {
      const d = new Date(`${baseDate}T${formData.checkInTime}`);
      ciTs = Timestamp.fromDate(d);
    }
    if (formData.checkOutTime) {
      const d = new Date(`${baseDate}T${formData.checkOutTime}`);
      coTs = Timestamp.fromDate(d);
    }

    const payload = {
      empId: formData.empId,
      date: formData.date,
      checkInTime: ciTs,
      checkOutTime: coTs,
      status: formData.status || null
    };

    // Also calculate 'hours' string manually if needed by legacy, 
    // but the app seems to compute it on the fly from timestamps now in 'computeRowHours'.
    // If we want to persist rough hours just in case:
    if (ciTs && coTs) {
      const diffMins = (coTs.seconds - ciTs.seconds) / 60;
      const h = diffMins / 60;
      if (h > 0) payload.hours = h; // persist as number or string? code supports both
    }

    try {
      setSaving(true);
      if (modalMode === "Add") {
        await addDoc(collection(db, "attendance"), payload);
      } else {
        await updateDoc(doc(db, "attendance", selectedId), payload);
      }
      setShowModal(false);
      handleRefresh();
    } catch (e) {
      console.error("Error saving document: ", e);
      alert("Failed to save record");
    } finally {
      setSaving(false);
    }
  };

  // ===============================
  // SYNC SUNDAYS
  // ===============================
  const handleOpenSyncModal = () => {
    setShowSyncModal(true);
  };

  const handleExecuteSync = async () => {
    if (!syncMonth) {
      alert("Please select a month");
      return;
    }
    const [yearStr, monthStr] = syncMonth.split('-');
    const year = parseInt(yearStr);
    const month = parseInt(monthStr) - 1; // 0-indexed
    const selectedDate = new Date(year, month);
    const monthName = selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' });

    if (!window.confirm(`Auto-fill attendance for Sundays in ${monthName}?`)) return;

    setLoading(true);
    try {
      const now = new Date();
      // Determine the last day to check. 
      // If selected month is current month, check up to today.
      // If selected month is past, check whole month.
      // If selected month is future, we strictly shouldn't fill it, or maybe user wants to pre-fill?
      // User request said "not only previous month", implying flexibility. 
      // Let's assume standard behavior: fill whole month, but if it is current month, stop at today?
      // Actually, for "Custom Month", if I select January and today is Jan 20th, I probably only want up to Jan 20th to avoid future false data.
      // But if I select December, I want all Sundays.

      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const sundays = [];

      for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(year, month, d);

        // Safety: Don't fill future dates
        if (dateObj > now) break;

        if (dateObj.getDay() === 0) { // Sunday
          const y = dateObj.getFullYear();
          const m = String(dateObj.getMonth() + 1).padStart(2, '0');
          const da = String(dateObj.getDate()).padStart(2, '0');
          sundays.push(`${y}-${m}-${da}`);
        }
      }

      if (sundays.length === 0) {
        alert("No past Sundays found in selected month.");
        setLoading(false);
        return;
      }

      const empIds = Object.keys(employeesMap);
      const batch = writeBatch(db);
      let opCount = 0;

      empIds.forEach(empId => {
        sundays.forEach(dateStr => {
          const exists = rows.find(r => r.empId === empId && r.date === dateStr);
          if (!exists) {
            const ref = doc(collection(db, "attendance"));
            const [y, m, d] = dateStr.split('-').map(Number);

            const ci = new Date(y, m - 1, d, 10, 0, 0);
            const co = new Date(y, m - 1, d, 18, 30, 0);

            batch.set(ref, {
              empId,
              date: dateStr,
              checkInTime: Timestamp.fromDate(ci),
              checkOutTime: Timestamp.fromDate(co),
              status: 'P',
              hours: 8.5
            });
            opCount++;
          }
        });
      });

      if (opCount > 0) {
        await batch.commit();
        alert(`Successfully added ${opCount} Sunday records.`);
        handleRefresh();
      } else {
        alert("All Sundays are already marked.");
        setLoading(false);
      }
      setShowSyncModal(false);

    } catch (e) {
      console.error("Error syncing Sundays", e);
      alert("Failed to sync Sundays.");
      setLoading(false);
    }
  };

  // ===============================
  // SYNC HOLIDAY
  // ===============================
  const handleOpenHolidayModal = () => {
    setShowHolidayModal(true);
    setHolidayDate("");
  };

  const handleExecuteHolidaySync = async () => {
    if (!holidayDate) {
      alert("Please select a date");
      return;
    }
    if (!window.confirm(`Mark ${holidayDate} as a Holiday for all employees?`)) return;

    setLoading(true);
    try {
      const empIds = Object.keys(employeesMap);
      const batch = writeBatch(db);
      let opCount = 0;

      empIds.forEach(empId => {
        // Check if record exists for this empId and date
        const exists = rows.find(r => r.empId === empId && r.date === holidayDate);
        if (!exists) {
          const ref = doc(collection(db, "attendance"));
          const [y, m, d] = holidayDate.split('-').map(Number);

          const ci = new Date(y, m - 1, d, 10, 0, 0);
          const co = new Date(y, m - 1, d, 18, 30, 0);

          batch.set(ref, {
            empId,
            date: holidayDate,
            checkInTime: Timestamp.fromDate(ci),
            checkOutTime: Timestamp.fromDate(co),
            status: 'P',
            hours: 8.5
          });
          opCount++;
        }
      });

      if (opCount > 0) {
        await batch.commit();
        alert(`Successfully marked ${opCount} employees for Holiday.`);
        handleRefresh();
      } else {
        alert("All employees already have records for this date.");
        setLoading(false);
      }
      setShowHolidayModal(false);

    } catch (e) {
      console.error("Error syncing Holiday", e);
      alert("Failed to sync Holiday.");
      setLoading(false);
    }
  };

  // ===============================
  // FILTERS
  // ===============================
  let filteredRows = [...rows];

  if (nameFilter) {
    filteredRows = filteredRows.filter(r =>
      (employeesMap[r.empId] || "")
        .toLowerCase()
        .includes(nameFilter.toLowerCase())
    );
  }

  if (fromDate) {
    filteredRows = filteredRows.filter(r => r.date >= fromDate);
  }

  if (toDate) {
    filteredRows = filteredRows.filter(r => r.date <= toDate);
  }

  if (statusFilter !== "All") {
    filteredRows = filteredRows.filter(r => getDisplayStatus(r) === statusFilter);
  }

  // ===============================
  // PAGINATION
  // ===============================
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = filteredRows.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredRows.length / recordsPerPage);


  // ===============================
  // EXCEL EXPORT
  // ===============================
  const exportExcel = () => {
    const data = filteredRows.map((r, i) => {
      const { hours, ot } = computeRowHours(r);
      return {
        "#": i + 1,
        Employee: employeesMap[r.empId] || "Unknown",
        Date: r.date,
        "Check In": r.checkInTime?.toDate().toLocaleTimeString(),
        "Check Out": r.checkOutTime?.toDate().toLocaleTimeString(),
        Hours: hours != null ? `${hours.toFixed(2)} h` : (r.hours || "0h"),
        "OT Hours": ot != null ? `${ot.toFixed(2)} h` : "0h",
        Status: getDisplayStatus(r)
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, "attendance.xlsx");
  };

  if (loading) return <Loader />;

  return (
    <div className="container-fluid mt-4 mb-5 px-4" style={{ paddingBottom: "100px" }}>

      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>
          {view === "register" ? "Attendance Register" : view === "settings" ? "Attendance Settings" : "Attendance Records"}
        </h4>
        <div className="d-flex gap-2">
          <div className="btn-group shadow-sm">
            <button
              className={`btn btn-sm ${view === 'records' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('records')}
            >
              <i className="bi bi-list-ul me-1"></i> Records
            </button>
            <button
              className={`btn btn-sm ${view === 'register' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('register')}
            >
              <i className="bi bi-check2-square me-1"></i> Register
            </button>
            <button
              className={`btn btn-sm ${view === 'settings' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('settings')}
            >
              <i className="bi bi-gear me-1"></i> Settings
            </button>
            <button
              className={`btn btn-sm ${view === 'notifications' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('notifications')}
            >
              <i className="bi bi-bell me-1"></i> Notifications
            </button>
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            title="Refresh"
            onClick={handleRefresh}
          >
            <i className="bi bi-arrow-clockwise"></i>
          </button>
          {view === "records" && (
            <>
              <button className="btn btn-primary" onClick={handleAddNew}>
                Add Attendance
              </button>
              <button className="btn btn-warning text-dark" onClick={handleOpenSyncModal}>
                Sync Sundays
              </button>
              <button className="btn btn-info text-white" onClick={handleOpenHolidayModal}>
                Sync Holiday
              </button>
              <button className="btn btn-success" onClick={exportExcel}>
                Export to Excel
              </button>
            </>
          )}
          {(view === "records" && selectedIds.length > 0) && (
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              <i className="bi bi-trash me-1"></i> Delete ({selectedIds.length})
            </button>
          )}
        </div>
      </div>

      {view === "records" ? (
        <>
          {/* FILTERS */}
          <div className="card p-3 mb-3">
            <div className="row g-2">
              <div className="col-md-3">
                <input
                  className="form-control"
                  placeholder="Search employee"
                  value={nameFilter}
                  list="employeeOptions"
                  onChange={e => setNameFilter(e.target.value)}
                />
                <datalist id="employeeOptions">
                  {Object.values(employeesMap).map((name, idx) => (
                    <option key={idx} value={name} />
                  ))}
                </datalist>
              </div>

              <div className="col-md-2">
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option>All</option>
                  <option>P</option>
                  <option>A</option>
                  <option>H</option>
                </select>
              </div>

              <div className="col-md-3 text-end">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setNameFilter("");
                    setFromDate("");
                    setToDate("");
                    setStatusFilter("All");
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>



          {/* TABLE */}
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "40px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={currentRecords.length > 0 && currentRecords.every(r => selectedIds.includes(r.id))}
                    onChange={(e) => handleSelectAll(e.target.checked, currentRecords)}
                  />
                </th>
                <th>#</th>
                <th>Employee Name</th>
                <th>Date</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Hours</th>
                <th>OT Hours</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentRecords.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center">
                    No records found
                  </td>
                </tr>
              )}

              {currentRecords.map((r, i) => {
                const { hours, ot } = computeRowHours(r);
                const ds = getDisplayStatus(r);
                const badgeClass = ds === 'P' ? 'bg-success' : ds === 'H' ? 'bg-warning text-dark' : 'bg-danger';
                return (
                  <tr key={r.id} className={selectedIds.includes(r.id) ? "table-warning" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(r.id)}
                        onChange={() => handleToggleSelect(r.id)}
                      />
                    </td>
                    <td>{indexOfFirst + i + 1}</td>
                    <td>{employeesMap[r.empId] || "Unknown"}</td>
                    <td>{r.date}</td>
                    <td>{r.checkInTime?.toDate().toLocaleTimeString()}</td>
                    <td>{r.checkOutTime?.toDate().toLocaleTimeString()}</td>
                    <td>{hours != null ? `${hours.toFixed(2)} h` : (r.hours || "0h")}</td>
                    <td>{ot != null ? `${ot.toFixed(2)} h` : "0h"}</td>
                    <td>
                      <span className={`badge ${badgeClass}`}>
                        {ds}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEdit(r)}
                        title="Edit"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(r.id)}
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* PAGINATION */}
          {filteredRows.length > 0 && (
            <div className={"d-flex justify-content-between align-items-center mt-3"}>
              <div className="d-flex align-items-center gap-2">
                <span>Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={recordsPerPage}
                  onChange={(e) => {
                    setRecordsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>entries</span>
                <span className="ms-2 text-muted small">
                  Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredRows.length)} of {filteredRows.length}
                </span>
              </div>

              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                  </li>
                  {(() => {
                    return [...Array(totalPages)].map((_, i) => {
                      const p = i + 1;
                      if (
                        p === 1 ||
                        p === totalPages ||
                        (p >= currentPage - 2 && p <= currentPage + 2)
                      ) {
                        return (
                          <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                          </li>
                        );
                      } else if (
                        (p === currentPage - 3 && currentPage > 4) ||
                        (p === currentPage + 3 && currentPage < totalPages - 3)
                      ) {
                        return <li key={p} className="page-item disabled"><span className="page-link">...</span></li>;
                      }
                      return null;
                    });
                  })()}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}


          {/* MODAL */}
          {showModal && (
            <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{modalMode} Attendance</h5>
                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Employee</label>
                      <select
                        className="form-select"
                        value={formData.empId}
                        onChange={e => setFormData({ ...formData, empId: e.target.value })}
                      >
                        <option value="">Select Employee</option>
                        {Object.entries(employeesMap).map(([id, name]) => (
                          <option key={id} value={id}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.date}
                        onChange={e => setFormData({ ...formData, date: e.target.value })}
                      />
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Check In Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={formData.checkInTime}
                          onChange={e => setFormData({ ...formData, checkInTime: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Check Out Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={formData.checkOutTime}
                          onChange={e => setFormData({ ...formData, checkOutTime: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status (Optional Override)</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={e => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="">Auto-calculate</option>
                        <option value="P">Present (P)</option>
                        <option value="H">Half-Day (H)</option>
                        <option value="A">Absent (A)</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? <span className="spinner-border spinner-border-sm" /> : "Save changes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SYNC MODAL */}
          {showSyncModal && (
            <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Sync Sundays</h5>
                    <button type="button" className="btn-close" onClick={() => setShowSyncModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <label className="form-label">Select Month</label>
                    <input
                      type="month"
                      className="form-control"
                      value={syncMonth}
                      onChange={(e) => setSyncMonth(e.target.value)}
                    />
                    <div className="form-text mt-2">
                      This will auto-fill attendance (10:00 AM - 6:30 PM) for all Sundays in the selected month up to today.
                      Existing records will be skipped.
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowSyncModal(false)}>Close</button>
                    <button type="button" className="btn btn-warning" onClick={handleExecuteSync}>Sync Now</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* HOLIDAY MODAL */}
          {showHolidayModal && (
            <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Sync Holiday</h5>
                    <button type="button" className="btn-close" onClick={() => setShowHolidayModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <label className="form-label">Select Holiday Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={holidayDate}
                      onChange={(e) => setHolidayDate(e.target.value)}
                    />
                    <div className="form-text mt-2">
                      This will mark all employees as "Present" (10:00 AM - 6:30 PM) for this specific date.
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowHolidayModal(false)}>Close</button>
                    <button type="button" className="btn btn-info text-white" onClick={handleExecuteHolidaySync}>Mark as Holiday</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : view === "register" ? (
        /* REGISTER VIEW */
        <div className="card shadow-sm p-4 animate__animated animate__fadeIn">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <label className="fw-bold fs-5">Attendance Date:</label>
              <input
                type="date"
                className="form-control"
                style={{ width: "200px" }}
                value={registerDate}
                onChange={e => setRegisterDate(e.target.value)}
              />
            </div>
            <button
              className="btn btn-success btn-lg px-5 shadow-sm"
              onClick={handleRegisterSave}
              disabled={saving}
            >
              <i className="bi bi-shield-check me-2"></i>
              {saving ? "Saving..." : "Save Daily Attendance"}
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-hover border align-middle">
              <thead className="table-light">
                <tr className="text-center">
                  <th className="text-start" style={{ width: "300px" }}>Employee Name</th>
                  <th>Present (P)</th>
                  <th>Absent (A)</th>
                  <th>Half Day (H)</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                </tr>
              </thead>
              <tbody>
                {employeesList.map(emp => {
                  const data = registerData[emp.empId] || { status: "", checkIn: "10:00", checkOut: "18:30" };
                  return (
                    <tr key={emp.empId} className={data.status === 'P' ? 'table-success-subtle' : data.status === 'A' ? 'table-danger-subtle' : ''}>
                      <td className="fw-bold">{emp.name}</td>
                      <td className="text-center">
                        <input
                          type="radio"
                          name={`status-${emp.empId}`}
                          className="btn-check"
                          id={`p-${emp.empId}`}
                          checked={data.status === 'P'}
                          onChange={() => setRegisterData({ ...registerData, [emp.empId]: { ...data, status: 'P' } })}
                        />
                        <label className="btn btn-outline-success border-0 rounded-circle" htmlFor={`p-${emp.empId}`}>
                          <i className={`bi ${data.status === 'P' ? 'bi-check-circle-fill' : 'bi-circle'} fs-3`}></i>
                        </label>
                      </td>
                      <td className="text-center">
                        <input
                          type="radio"
                          name={`status-${emp.empId}`}
                          className="btn-check"
                          id={`a-${emp.empId}`}
                          checked={data.status === 'A'}
                          onChange={() => setRegisterData({ ...registerData, [emp.empId]: { ...data, status: 'A' } })}
                        />
                        <label className="btn btn-outline-danger border-0 rounded-circle" htmlFor={`a-${emp.empId}`}>
                          <i className={`bi ${data.status === 'A' ? 'bi-x-circle-fill' : 'bi-circle'} fs-3`}></i>
                        </label>
                      </td>
                      <td className="text-center">
                        <input
                          type="radio"
                          name={`status-${emp.empId}`}
                          className="btn-check"
                          id={`h-${emp.empId}`}
                          checked={data.status === 'H'}
                          onChange={() => setRegisterData({ ...registerData, [emp.empId]: { ...data, status: 'H' } })}
                        />
                        <label className="btn btn-outline-warning border-0 rounded-circle" htmlFor={`h-${emp.empId}`}>
                          <i className={`bi ${data.status === 'H' ? 'bi-dash-circle-fill' : 'bi-circle'} fs-3`}></i>
                        </label>
                      </td>
                      <td>
                        {data.status !== 'A' && (
                          <input
                            type="time"
                            className="form-control form-control-sm"
                            value={data.checkIn}
                            onChange={e => setRegisterData({ ...registerData, [emp.empId]: { ...data, checkIn: e.target.value } })}
                          />
                        )}
                      </td>
                      <td>
                        {data.status !== 'A' && (
                          <input
                            type="time"
                            className="form-control form-control-sm"
                            value={data.checkOut}
                            onChange={e => setRegisterData({ ...registerData, [emp.empId]: { ...data, checkOut: e.target.value } })}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === "settings" ? (
        <Settings />
      ) : view === "notifications" ? (
        /* NOTIFICATIONS VIEW */
        <div className="card shadow-sm p-4 animate__animated animate__fadeIn">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-bold mb-0 text-primary"><i className="bi bi-bell me-2"></i> Attendance Reminders</h5>
            <div className="d-flex align-items-center gap-3">
              <label className="text-muted fw-bold small">Check Date:</label>
              <input
                type="date"
                className="form-control form-control-sm border-primary"
                value={notifDateFilter}
                onChange={e => setNotifDateFilter(e.target.value)}
              />
            </div>
          </div>

          <div className="alert alert-info border-0 shadow-sm d-flex align-items-center mb-4">
            <i className="bi bi-info-circle-fill me-3 fs-4"></i>
            <div>
              <strong>In-App Notifications</strong><br />
              <span className="small">Send reminders to employees who haven't logged their attendance. The notification will appear as a banner when they log into their portal.</span>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover border align-middle bg-white">
              <thead className="table-light">
                <tr>
                  <th>Employee Name</th>
                  <th>Status on {notifDateFilter}</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {employeesList.map(emp => {
                  const hasRecord = rows.some(r => r.empId === emp.empId && r.date === notifDateFilter);
                  
                  // If they have a record, no need to remind (unless we want to remind everyone, but let's filter)
                  
                  return (
                    <tr key={emp.empId} className={hasRecord ? "opacity-50" : "table-warning-subtle"}>
                      <td className="fw-bold">
                        {emp.name}
                        {hasRecord && <span className="badge bg-success ms-2">Logged</span>}
                      </td>
                      <td>
                        {hasRecord ? (
                          <span className="text-success"><i className="bi bi-check-circle me-1"></i>Record Exists</span>
                        ) : (
                          <span className="text-danger fw-bold"><i className="bi bi-x-circle me-1"></i>Missing Record</span>
                        )}
                      </td>
                      <td className="text-end">
                        <button
                          className={`btn btn-sm shadow-sm ${hasRecord ? 'btn-outline-secondary' : 'btn-primary'}`}
                          onClick={async () => {
                            if (!window.confirm(`Send in-app reminder to ${emp.name}?`)) return;
                            setNotifSendingId(emp.empId);
                            try {
                              await addDoc(collection(db, "SYSTEM_NOTIFICATIONS"), {
                                targetUid: emp.empId,
                                title: "Attendance Reminder",
                                message: `Please log your attendance for ${notifDateFilter}.`,
                                type: "ATTENDANCE_REMINDER",
                                isRead: false,
                                createdAt: Timestamp.now(),
                                relatedDate: notifDateFilter
                              });
                              alert(`Reminder sent to ${emp.name}`);
                            } catch(err) {
                              console.error("Error sending notification:", err);
                              alert("Failed to send reminder.");
                            }
                            setNotifSendingId(null);
                          }}
                          disabled={notifSendingId === emp.empId || hasRecord}
                        >
                          {notifSendingId === emp.empId ? (
                            <span className="spinner-border spinner-border-sm me-1" />
                          ) : (
                            <i className="bi bi-send me-1"></i>
                          )}
                          Send Reminder
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Attendance;
